/** biome-ignore-all lint/suspicious/noAssignInExpressions: <explanation> */
import { SitumApiPermissionLevel } from "@situm/sdk-js";
import merge from "deepmerge";

import {
  EditUI,
  selectAuthReady,
  selectCurrentOrganization,
  setAuthReady,
  setEditMode,
} from "../app/appSlice";
import { isolateLayer } from "../editor/editorSlice";
import {
  setNavigationFrom,
  setNavigationTo,
  setSpeakAloudIndications,
} from "../navigation/navigationSlice";
import {
  setProfileName,
  setProfileOrganizationId,
} from "../session/sessionSlice";
import { setReceivedSessionId } from "../share_location/shareLocationSlice";
import {
  hydrateUserPreferencesColorTheme,
  UserPreferenceKeys,
} from "../userPreferences/userPreferencesSlice";
import {
  defaultConfig,
  setApiKey,
  setBearing,
  setBuildingId,
  setConfigItem,
  setDomain,
  setFloorId,
  setInitialConfigItem,
  setPitch,
  setZoom,
} from "./configSlice";

import { DEFAULT_CONFIG_DOMAIN, NEW_CONFIG_PATH } from "@/constants";
import store from "../../app/store";
import { LOCALSTORAGE_PREFIX } from "../app/constants";
import type { SearchParamsSitum } from "../app/types";
import { RouteType } from "../directions/types";
import { OverlayFeatureTypes, type Profile } from "../editor/types";
import { SHARED_SESSION_ID_KEY } from "../share_location";
import { resolveInitialColorThemes } from "../userPreferences/colorTheme";
import { getInitialValue } from "../userPreferences/initialValues";
import {
  cleanJSON,
  difference,
  isEmptyObject,
  joinUrlPath,
  safeLocalStorage,
} from "../utils/utils";
import { verifyOrganization } from "../webview_api/utils";
import {
  CartographyToShow,
  type ConfigState,
  DEFAULT_SHARE_PROFILE,
  type LocationQualityThreslholds,
  type LocationUpdateThesholds,
  MapAttributionsTypes,
  PrefetchMode,
} from "./types";

export const loadConfig = async (
  searchParams: URLSearchParams,
  profileURL: string | null,
  configDomain: string | null,
) => {
  const domain =
    searchParams.get("domain" satisfies SearchParamsSitum) ||
    store.getState().config.auth.domain;
  const newConfigEndpoint = `https://${domain}${NEW_CONFIG_PATH}`;

  // Fetch the remote profile data. Expect the full Profile object
  // to access organizationId for cross-organization validation.
  let remoteResponse: Partial<ConfigState> | Profile = {};
  if (profileURL) {
    remoteResponse = await fetchRemoteProfile(profileURL, newConfigEndpoint);
    // fallback: if not on our custom api use blob storage
    if (isEmptyObject(remoteResponse)) {
      remoteResponse = await fetchRemoteProfile(
        `${profileURL}.json`,
        configDomain || DEFAULT_CONFIG_DOMAIN,
      );
    }
  }

  let remoteConfig = {};
  let profileOrganizationId: string | null = null;
  let profileName: string | null = null;

  // Extract content and organizationId
  if (
    profileURL &&
    !profileURL.includes(".json") &&
    "content" in remoteResponse
  ) {
    remoteConfig = remoteResponse.content;
    profileOrganizationId = remoteResponse.organizationId ?? null;
    profileName = profileURL;
  } else if (!isEmptyObject(remoteResponse)) {
    remoteConfig = remoteResponse;
    profileName = profileURL;
  }

  // fallback profile if no auth/profile specified on query params
  const apikeyParam = searchParams.get("apikey" satisfies SearchParamsSitum);
  const waitForAuth =
    searchParams.get("wait_for_auth" satisfies SearchParamsSitum) === "true";
  if (
    !apikeyParam &&
    !waitForAuth &&
    (!profileURL || isEmptyObject(remoteConfig))
  ) {
    const fallbackProfile = (
      (await fetchRemoteProfile(
        import.meta.env.VITE_FALLBACK_PROFILE ?? "",
        newConfigEndpoint,
      )) as Profile
    ).content;
    remoteConfig = fallbackProfile;
    profileName = null;
  }

  const configFromURLParams = fetchConfigFromURLParams(searchParams);

  let newConfig = merge(
    cleanJSON(remoteConfig),
    // @ts-expect-error
    cleanJSON(configFromURLParams),
  );

  newConfig = merge(
    defaultConfig,
    // @ts-expect-error
    difference(defaultConfig, cleanJSON(newConfig)),
    { arrayMerge: overwriteArrays },
  );

  // Store the organizationId of the profile so we can check it later
  // against the authenticated user's organization.
  store.dispatch(setProfileOrganizationId(profileOrganizationId));
  // Store a *valid* url profile name
  // if VITE_FALLBACK_PROFILE is used means url's profile is no valid, so this value is null
  store.dispatch(setProfileName(profileName));

  // @ts-expect-error
  return processConfig(newConfig);
};

function overwriteArrays(
  target: unknown[],
  source: unknown[],
  options?: merge.ArrayMergeOptions | undefined,
): unknown[] {
  if (Array.isArray(target) && Array.isArray(source)) {
    return source; // Replace the target array with the source array
  }

  // Use the default behavior for other cases
  return merge(target, source, options);
}

// Fetches a profile from a remote URL.
// Returns the full Profile object (metadata + content).
export const fetchRemoteProfile = async (
  profileName: string | null,
  profileDomain: string | null,
): Promise<Partial<ConfigState> | Profile> => {
  if (!profileName || !profileDomain) return {};

  const url = joinUrlPath(profileDomain, `${profileName}`);

  const response: Profile = await fetch(url)
    .then((response) => {
      if (!response.ok) {
        console.error("Could not find remote profile");
        return {};
      }
      return response.json();
    })
    .catch((): any => {
      console.error("Could not find remote profile");
      return null;
    });

  return response;
};

export const fetchConfigFromURLParams = (
  searchParams: URLSearchParams,
): ConfigState => {
  const config: any = {
    auth: {},
    camera: {},
    i18n: {},
    internal: {},
    map: { initialView: {} },
    ui: { widget_visibility: {} },
  };

  // Auth
  let api_key_param = searchParams.get("apikey" satisfies SearchParamsSitum);
  const domain_param = searchParams.get("domain" satisfies SearchParamsSitum);
  if (
    searchParams.get("wait_for_auth" satisfies SearchParamsSitum) === "true"
  ) {
    api_key_param = "";
  }
  api_key_param !== null && (config.auth.api_key = api_key_param);
  domain_param && (config.auth.domain = domain_param);

  // Shared Session ID
  const sharedSessionId = searchParams.get(
    "shared_session_id" satisfies SearchParamsSitum,
  );

  if (sharedSessionId) {
    store.dispatch(setReceivedSessionId(sharedSessionId));
  } else {
    const storedSessionId = safeLocalStorage.getItem(SHARED_SESSION_ID_KEY);
    if (storedSessionId) {
      try {
        const parsedSessionId = JSON.parse(storedSessionId) as string;
        if (parsedSessionId) {
          store.dispatch(setReceivedSessionId(parsedSessionId));
        }
      } catch (e) {
        console.warn("Failed to parse stored session id", e);
      }
    }
  }

  // Location

  // Modes
  const embed =
    searchParams.get("mode" satisfies SearchParamsSitum) === "embed";
  embed && (config.internal.embed = embed);
  const debug =
    searchParams.get("debug" satisfies SearchParamsSitum) === "true";
  debug && (config.internal.debug = true);
  const simulator =
    searchParams.get("simulator" satisfies SearchParamsSitum) === "true";
  simulator && (config.internal.simulator = true);

  // i18n
  const rtl = searchParams.get("rtl" satisfies SearchParamsSitum) === "true";
  rtl && (config.ui.rtl = rtl);
  const language = searchParams.get("lng" satisfies SearchParamsSitum);
  language && (config.i18n.language = language);

  // Device Id
  const deviceId = searchParams.get("device_id" satisfies SearchParamsSitum);
  deviceId && (config.internal.deviceId = deviceId);

  // Navigation params
  const navigationFromURL =
    Number(searchParams.get("navigation_from" satisfies SearchParamsSitum)) ||
    null;
  const navigationToURL =
    Number(searchParams.get("navigation_to" satisfies SearchParamsSitum)) ||
    null;
  navigationFromURL && store.dispatch(setNavigationFrom(navigationFromURL));
  navigationToURL && store.dispatch(setNavigationTo(navigationToURL));

  // Map

  const buildingId =
    Number(searchParams.get("building_id" satisfies SearchParamsSitum)) || null;
  const floorId =
    Number(searchParams.get("floor_id" satisfies SearchParamsSitum)) || null;
  const lockCameraToBuilding = searchParams.has(
    "lock_camera_to_building" satisfies SearchParamsSitum,
  )
    ? searchParams.get(
        "lock_camera_to_building" satisfies SearchParamsSitum,
      ) === "true"
    : null;

  buildingId && (config.map.initialView.building_id = buildingId);
  floorId && (config.map.initialView.floor_id = floorId);
  lockCameraToBuilding !== null &&
    lockCameraToBuilding !== undefined &&
    (config.camera.lockToBuilding = lockCameraToBuilding);
  if (!Object.keys(config.map.initialView)) {
    delete config.map.initialView;
  }

  // UI params
  const dashboardEmbed =
    searchParams.get("dashboard_embed" satisfies SearchParamsSitum) === "true";
  config.internal.dashboardEmbed = dashboardEmbed;

  if (dashboardEmbed) {
    store.dispatch(setEditMode(EditUI.overlayLayers));
  }

  const editor = searchParams.get("editor" satisfies SearchParamsSitum);

  if (editor) {
    // Check if editor variable is a valid OverlayFeatureTypes item
    if (editor === "paths") {
      store.dispatch(setEditMode(EditUI.paths));
    } else if (editor === "settings") {
      store.dispatch(setEditMode(EditUI.settings));
    } else if (editor === "imdf") {
      store.dispatch(setEditMode(EditUI.baseLayers));
    } else if (editor === "true") {
      store.dispatch(setEditMode(EditUI.overlayLayers));
    } else if (editor in OverlayFeatureTypes) {
      store.dispatch(setEditMode(EditUI.overlayLayers));
      store.dispatch(isolateLayer(editor as OverlayFeatureTypes));
    }
  }

  const whiteLabel = searchParams.get("wl" satisfies SearchParamsSitum);
  const fixedPOI = searchParams.get("fp" satisfies SearchParamsSitum) || null;
  const hideValues =
    searchParams.get("hide" satisfies SearchParamsSitum)?.split(",") || [];

  whiteLabel !== null && (config.ui.whiteLabel = whiteLabel === "true");
  fixedPOI && (config.map.initialView.fixedPoi = Number(fixedPOI));
  searchParams.get("hpc" satisfies SearchParamsSitum) === "true" &&
    (config.ui.widget_visibility.poi_categories = false);
  hideValues.some((x) => x === "mc") &&
    (config.ui.widget_visibility.map_control = false);
  hideValues.some((x) => x === "lc") &&
    (config.ui.widget_visibility.location_control = false);
  hideValues.some((x) => x === "ec") &&
    (config.ui.widget_visibility.exploration_control = false);
  hideValues.some((x) => x === "pn") &&
    (config.ui.widget_visibility.poi_names = false);
  hideValues.some((x) => x === "sb") &&
    (config.ui.widget_visibility.search_bar = false);
  hideValues.some((x) => x === "pcl") &&
    (config.ui.widget_visibility.poi_cluster = false);
  hideValues.some((x) => x === "pc") &&
    (config.ui.widget_visibility.poi_categories = false);
  hideValues.some((x) => x === "rts") &&
    (config.ui.widget_visibility.route_type_selector = false);

  if (!Object.keys(config.ui.widget_visibility)) {
    delete config.ui.widget_visibility;
  }

  return config;
};

export const processConfig = async (config: ConfigState) => {
  // Auth
  config.auth?.api_key && store.dispatch(setApiKey(config.auth.api_key));
  config.auth?.production_api_key !== undefined &&
    config.auth?.production_api_key !== null &&
    store.dispatch(
      setInitialConfigItem(
        "auth.production_api_key",
        config.auth.production_api_key,
      ),
    );
  config.auth?.domain && store.dispatch(setDomain(config.auth.domain));

  // Camera
  config.camera?.maxZoom &&
    store.dispatch(
      setInitialConfigItem("camera.maxZoom", config.camera.maxZoom),
    );
  config.camera?.minZoom &&
    store.dispatch(
      setInitialConfigItem("camera.minZoom", config.camera.minZoom),
    );
  config.camera?.lockToBuilding !== undefined &&
    config.camera?.lockToBuilding !== null &&
    store.dispatch(
      setInitialConfigItem(
        "camera.lockToBuilding",
        config.camera.lockToBuilding,
      ),
    );
  config.camera?.paddingVertical !== undefined &&
    config.camera?.paddingVertical !== null &&
    store.dispatch(
      setInitialConfigItem(
        "camera.paddingVertical",
        config.camera.paddingVertical,
      ),
    );
  config.camera?.paddingHorizontal !== undefined &&
    config.camera?.paddingHorizontal !== null &&
    store.dispatch(
      setInitialConfigItem(
        "camera.paddingHorizontal",
        config.camera.paddingHorizontal,
      ),
    );
  config.camera?.allowCameraAutoFitToRoute !== undefined &&
    config.camera?.allowCameraAutoFitToRoute !== null &&
    store.dispatch(
      setInitialConfigItem(
        "camera.allowCameraAutoFitToRoute",
        config.camera.allowCameraAutoFitToRoute,
      ),
    );

  // Modes
  config.internal?.embed &&
    store.dispatch(
      setInitialConfigItem("internal.embed", config.internal.embed),
    );
  config.internal?.dashboardEmbed &&
    store.dispatch(
      setInitialConfigItem(
        "internal.dashboardEmbed",
        config.internal.dashboardEmbed,
      ),
    );
  config.internal?.debug &&
    store.dispatch(
      setInitialConfigItem("internal.debug", config.internal.debug),
    );
  config.internal?.profile &&
    store.dispatch(
      setInitialConfigItem("internal.profile", config.internal.profile),
    );
  config.internal?.deviceId &&
    store.dispatch(
      setInitialConfigItem("internal.deviceId", config.internal.deviceId),
    );
  config.internal?.simulator &&
    store.dispatch(
      setInitialConfigItem("internal.simulator", config.internal.simulator),
    );
  config.internal?.appName &&
    store.dispatch(
      setInitialConfigItem("internal.appName", config.internal.appName),
    );
  config.internal?.crawlersAllowIndex !== undefined &&
    config.internal?.crawlersAllowIndex !== null &&
    store.dispatch(
      setInitialConfigItem(
        "internal.crawlersAllowIndex",
        config.internal?.crawlersAllowIndex,
      ),
    );
  config.internal?.disableNavigationEvents !== undefined &&
    config.internal?.disableNavigationEvents !== null &&
    store.dispatch(
      setInitialConfigItem(
        "internal.disableNavigationEvents",
        config.internal?.disableNavigationEvents,
      ),
    );

  // Layers
  config.internal?.showModels !== undefined &&
    store.dispatch(
      setInitialConfigItem("internal.showModels", config.internal.showModels),
    );
  config.internal?.showPois !== undefined &&
    store.dispatch(
      setInitialConfigItem("internal.showPois", config.internal.showPois),
    );
  config.internal?.refreshRate &&
    store.dispatch(
      setInitialConfigItem("internal.refreshRate", config.internal.refreshRate),
    );
  config.internal?.refreshDate &&
    store.dispatch(
      setInitialConfigItem("internal.refreshDate", config.internal.refreshDate),
    );
  config.internal?.limitedRefresh !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "internal.limitedRefresh",
        config.internal.limitedRefresh,
      ),
    );
  config.internal?.tryRefreshOnFirstLoad !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "internal.tryRefreshOnFirstLoad",
        config.internal.tryRefreshOnFirstLoad,
      ),
    );

  typeof config.internal?.autoResetView?.enabled === "boolean" &&
    store.dispatch(
      setInitialConfigItem(
        "internal.autoResetView.enabled",
        config.internal.autoResetView.enabled,
      ),
    );
  config.internal?.autoResetView?.idleTime > 0 &&
    store.dispatch(
      setInitialConfigItem(
        "internal.autoResetView.idleTime",
        config.internal.autoResetView.idleTime,
      ),
    );

  // Location quality
  const thresholds: Array<keyof LocationQualityThreslholds> = [
    "accuracyThreshold",
    "locationsWithoutBearingThreshold",
    "maxJumpAllowedThreshold",
    "minimumWalkThreshold",
  ];

  thresholds.forEach((threshold) => {
    const value = config.internal?.locationQualityThresholds?.[threshold];
    value !== undefined &&
      value !== null &&
      store.dispatch(
        setInitialConfigItem(
          `internal.locationQualityThresholds.${threshold}`,
          value,
        ),
      );
  });

  // Location update thresholds
  const updateThresholds: Array<keyof LocationUpdateThesholds> = [
    "accuracy",
    "bearing",
    "distance",
    "animationDuration",
  ];

  updateThresholds.forEach((threshold) => {
    const value = config.internal?.locationUpdateThresholds?.[threshold];
    value !== undefined &&
      value !== null &&
      store.dispatch(
        setInitialConfigItem(
          `internal.locationUpdateThresholds.${threshold}`,
          value,
        ),
      );
  });

  const customMinUpdateInterval =
    config.internal?.locationUpdateThresholds?.minUpdateIntervalMs;

  if (customMinUpdateInterval != null) {
    store.dispatch(
      setInitialConfigItem(
        "internal.locationUpdateThresholds.minUpdateIntervalMs",
        Math.max(1000, customMinUpdateInterval),
      ),
    );
  }

  config.internal?.tts?.engine !== undefined &&
    store.dispatch(
      setInitialConfigItem("internal.tts.engine", config.internal.tts.engine),
    );

  config.i18n?.language &&
    store.dispatch(setInitialConfigItem("i18n.language", config.i18n.language));
  config.i18n?.selectableLanguages &&
    store.dispatch(
      setInitialConfigItem(
        "i18n.selectableLanguages",
        config.i18n.selectableLanguages,
      ),
    );

  config.i18n?.measurementSystem &&
    store.dispatch(
      setInitialConfigItem(
        "i18n.measurementSystem",
        config.i18n.measurementSystem,
      ),
    );

  // Map
  config.map?.interactive !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.interactive", config.map.interactive),
    );

  config.map?.lights?.anchor !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.lights.anchor", config.map.lights.anchor),
    );
  config.map?.lights?.main?.color !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.main.color",
        config.map.lights.main.color,
      ),
    );
  config.map?.lights?.main?.intensity !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.main.intensity",
        config.map.lights.main.intensity,
      ),
    );

  config.map?.lights?.fill?.color !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.fill.color",
        config.map.lights.fill.color,
      ),
    );
  config.map?.lights?.fill?.intensity !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.fill.intensity",
        config.map.lights.fill.intensity,
      ),
    );

  config.map?.lights?.hemisphere?.skyColor !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.hemisphere.skyColor",
        config.map.lights.hemisphere.skyColor,
      ),
    );
  config.map?.lights?.hemisphere?.groundColor !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.hemisphere.groundColor",
        config.map.lights.hemisphere.groundColor,
      ),
    );
  config.map?.lights?.hemisphere?.intensity !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.lights.hemisphere.intensity",
        config.map.lights.hemisphere.intensity,
      ),
    );

  config.map?.shadows?.enabled !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.shadows.enabled", config.map.shadows.enabled),
    );
  config.map?.shadows?.cameraFarFactor !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.cameraFarFactor",
        config.map.shadows.cameraFarFactor,
      ),
    );
  config.map?.shadows?.cameraFarMin !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.cameraFarMin",
        config.map.shadows.cameraFarMin,
      ),
    );
  config.map?.shadows?.mapSize !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.shadows.mapSize", config.map.shadows.mapSize),
    );
  config.map?.shadows?.bias !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.shadows.bias", config.map.shadows.bias),
    );
  config.map?.shadows?.normalBias !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.normalBias",
        config.map.shadows.normalBias,
      ),
    );
  config.map?.shadows?.radius !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.shadows.radius", config.map.shadows.radius),
    );
  config.map?.shadows?.planeOpacity !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.planeOpacity",
        config.map.shadows.planeOpacity,
      ),
    );
  config.map?.shadows?.lodMinZoom !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.lodMinZoom",
        config.map.shadows.lodMinZoom,
      ),
    );
  config.map?.shadows?.receiverSize !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.shadows.receiverSize",
        config.map.shadows.receiverSize,
      ),
    );

  config.map?.renderer?.toneMapping !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.renderer.toneMapping",
        config.map.renderer.toneMapping,
      ),
    );
  config.map?.renderer?.toneMappingExposure !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.renderer.toneMappingExposure",
        config.map.renderer.toneMappingExposure,
      ),
    );

  config.map?.newPoiLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.newPoiLayer", config.map.newPoiLayer),
    );
  config.map?.newIMDFLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.newIMDFLayer", config.map.newIMDFLayer),
    );
  config.map?.newLocationLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.newLocationLayer", config.map.newLocationLayer),
    );
  config.map?.newModelsLayers !== undefined &&
    store.dispatch(
      setInitialConfigItem("map.newModelsLayers", config.map.newModelsLayers),
    );
  config.map?.newIconTextLayers !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.newIconTextLayers",
        config.map.newIconTextLayers,
      ),
    );

  config.map?.newLocationBehaviour !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.newLocationBehaviour",
        config.map.newLocationBehaviour,
      ),
    );

  config.map?.newRouteLabelLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.newRouteLabelLayer",
        config.map.newRouteLabelLayer,
      ),
    );

  config.map?.newFloorplanLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.newFloorplanLayer",
        config.map.newFloorplanLayer,
      ),
    );

  config.map?.newRouteIndicationLayer !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.newRouteIndicationLayer",
        config.map.newRouteIndicationLayer,
      ),
    );

  if (config.map?.cartographyToShow && config.map?.useTiles !== undefined) {
    // TODO: Remove, this is here for compatibility
    const updatedCartographyToShow = [...config.map.cartographyToShow];
    config.map.useTiles &&
      !updatedCartographyToShow.includes(CartographyToShow.TILES) &&
      updatedCartographyToShow.push(CartographyToShow.TILES);
  }

  const iv = config.map?.initialView;

  if (typeof iv?.building_id === "number")
    store.dispatch(setBuildingId(iv.building_id));

  if (typeof iv?.floor_id === "number") store.dispatch(setFloorId(iv.floor_id));

  if (typeof iv?.zoom === "number") store.dispatch(setZoom(iv.zoom));

  if (typeof iv?.bearing === "number") store.dispatch(setBearing(iv.bearing));

  if (typeof iv?.pitch === "number") store.dispatch(setPitch(iv.pitch));

  if (typeof iv?.latitude === "number")
    store.dispatch(
      setInitialConfigItem("map.initialView.latitude", iv.latitude),
    );

  if (typeof iv?.longitude === "number")
    store.dispatch(
      setInitialConfigItem("map.initialView.longitude", iv.longitude),
    );

  // TODO: Remove, this is here for compatibility
  if (
    config.map?.cartographyToShow &&
    config.map?.basemap?.active !== undefined
  ) {
    const updatedCartographyToShow = [...config.map.cartographyToShow];
    // TODO: Remove, this is here for compatibility
    config.map.basemap?.active &&
      !updatedCartographyToShow.includes(CartographyToShow.BASEMAP) &&
      updatedCartographyToShow.push(CartographyToShow.BASEMAP);
  }

  config.map?.basemap?.custom_style_url !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.basemap.custom_style_url",
        config.map.basemap.custom_style_url,
      ),
    );

  config.map?.basemap?.custom_style_high_contrast_dark_url !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.basemap.custom_style_high_contrast_dark_url",
        config.map.basemap.custom_style_high_contrast_dark_url,
      ),
    );

  config.map?.cartographyToShow !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.cartographyToShow",
        config.map.cartographyToShow,
      ),
    );

  // UI
  config.ui?.rtl &&
    store.dispatch(setInitialConfigItem("ui.rtl", config.ui?.rtl));
  config.ui?.whiteLabel !== undefined &&
    store.dispatch(setInitialConfigItem("ui.whiteLabel", config.ui.whiteLabel));

  config.ui?.header_image !== undefined &&
    store.dispatch(
      setInitialConfigItem("ui.header_image", config.ui.header_image),
    );
  config.ui?.map_attribution !== undefined &&
    store.dispatch(
      setInitialConfigItem("ui.map_attribution", config.ui.map_attribution),
    );
  // Retrocompatibility
  //@ts-expect-error
  (config.ui?.map_attribution === "both" ||
    //@ts-expect-error
    config.ui?.map_attribution === "mapbox") &&
    store.dispatch(
      setInitialConfigItem("ui.map_attribution", MapAttributionsTypes.situm),
    );

  // Fixed poi
  // Keep compatibility
  //@ts-expect-error
  config.ui?.fixedPOI !== undefined &&
    store.dispatch(
      //@ts-expect-error
      setInitialConfigItem("map.initialView.fixedPoi", config.ui.fixedPOI),
    );

  config.map?.initialView?.fixedPoi !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "map.initialView.fixedPoi",
        config.map.initialView.fixedPoi,
      ),
    );

  config.styles?.fixedPoi?.showIcon !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.showIcon",
        config.styles.fixedPoi.showIcon,
      ),
    );

  config.styles?.fixedPoi?.showName !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.showName",
        config.styles.fixedPoi.showName,
      ),
    );

  config.styles?.fixedPoi?.showLabel !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.showLabel",
        config.styles.fixedPoi.showLabel,
      ),
    );

  config.styles?.fixedPoi?.outlineColor &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.outlineColor",
        config.styles.fixedPoi.outlineColor,
      ),
    );

  config.styles?.fixedPoi?.backgroundColor &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.backgroundColor",
        config.styles.fixedPoi.backgroundColor,
      ),
    );

  config.styles?.fixedPoi?.textColor &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.textColor",
        config.styles.fixedPoi.textColor,
      ),
    );

  config.styles?.fixedPoi?.fontSize &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.fontSize",
        config.styles.fixedPoi.fontSize,
      ),
    );

  config.styles?.fixedPoi?.textOutlineColor &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.textOutlineColor",
        config.styles.fixedPoi.textOutlineColor,
      ),
    );

  config.styles?.fixedPoi?.borderColor &&
    store.dispatch(
      setInitialConfigItem(
        "styles.fixedPoi.borderColor",
        config.styles.fixedPoi.borderColor,
      ),
    );

  config.share?.baseUrl &&
    store.dispatch(
      setInitialConfigItem("share.baseUrl", config.share?.baseUrl),
    );

  config.share?.profile !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "share.profile",
        config.share.profile || DEFAULT_SHARE_PROFILE,
      ),
    );

  config.ui?.poi_category_filter_interface !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.poi_category_filter_interface",
        config.ui.poi_category_filter_interface,
      ),
    );

  config.ui?.poi_surname !== undefined &&
    store.dispatch(
      setInitialConfigItem("ui.poi_surname", config.ui.poi_surname),
    );
  config.ui?.widget_visibility?.map_control !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.map_control",
        config.ui.widget_visibility.map_control,
      ),
    );
  config.ui?.widget_visibility?.map_control_items !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.map_control_items",
        config.ui.widget_visibility.map_control_items,
      ),
    );
  config.ui?.widget_visibility?.location_control !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.location_control",
        config.ui.widget_visibility.location_control,
      ),
    );
  config.ui?.widget_visibility?.building_selector !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.building_selector",
        config.ui.widget_visibility.building_selector,
      ),
    );
  config.ui?.widget_visibility?.lock_floor_selector !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.lock_floor_selector",
        config.ui.widget_visibility.lock_floor_selector,
      ),
    );
  config.ui?.widget_visibility?.floor_selector !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.floor_selector",
        config.ui.widget_visibility.floor_selector,
      ),
    );
  config.ui?.widget_visibility?.language_control !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.language_control",
        config.ui.widget_visibility.language_control,
      ),
    );
  config.ui?.widget_visibility?.exploration_control !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.exploration_control",
        config.ui.widget_visibility.exploration_control,
      ),
    );
  config.ui?.widget_visibility?.poi_panel !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.poi_panel",
        config.ui.widget_visibility.poi_panel,
      ),
    );
  config.ui?.widget_visibility?.poi_names !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.poi_names",
        config.ui.widget_visibility.poi_names,
      ),
    );
  config.ui?.widget_visibility?.poi_names_navigation !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.poi_names_navigation",
        config.ui.widget_visibility.poi_names_navigation,
      ),
    );
  config.ui?.widget_visibility?.search_bar !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.search_bar",
        config.ui.widget_visibility.search_bar,
      ),
    );
  config.ui?.widget_visibility?.top_search_bar !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.top_search_bar",
        config.ui.widget_visibility.top_search_bar,
      ),
    );

  config.ui?.widget_visibility?.kiosk_qr_code !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.kiosk_qr_code",
        config.ui?.widget_visibility?.kiosk_qr_code,
      ),
    );

  config.ui?.widget_visibility?.directions_panel !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.directions_panel",
        config.ui.widget_visibility.directions_panel,
      ),
    );

  config.ui?.widget_visibility?.directions_shortcut !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.directions_shortcut",
        config.ui.widget_visibility.directions_shortcut,
      ),
    );

  config.ui?.widget_visibility?.routeinfo_tooltip !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.routeinfo_tooltip",
        config.ui.widget_visibility.routeinfo_tooltip,
      ),
    );

  config.ui?.widget_visibility?.zoom_buttons_mobile !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.zoom_buttons_mobile",
        config.ui.widget_visibility.zoom_buttons_mobile,
      ),
    );

  config.ui?.widget_visibility?.poi_categories !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.poi_categories",
        config.ui.widget_visibility.poi_categories,
      ),
    );
  config.ui?.widget_visibility?.navigation_indications !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.navigation_indications",
        config.ui.widget_visibility.navigation_indications,
      ),
    );
  config.ui?.widget_visibility?.navigation_info !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.navigation_info",
        config.ui.widget_visibility.navigation_info,
      ),
    );
  config.ui?.widget_visibility?.destination_reached_confirmation !==
    undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.destination_reached_confirmation",
        config.ui.widget_visibility.destination_reached_confirmation,
      ),
    );
  config.ui?.widget_visibility?.route_type_selector !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.route_type_selector",
        config.ui.widget_visibility.route_type_selector,
      ),
    );
  config.ui?.widget_visibility?.share_buttons !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.widget_visibility.share_buttons",
        config.ui.widget_visibility.share_buttons,
      ),
    );

  // Poi category order
  config.ui?.poiCategoriesOrder !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.poiCategoriesOrder",
        config.ui.poiCategoriesOrder,
      ),
    );

  config.ui?.user_settings_panel_items !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.user_settings_panel_items",
        config.ui.user_settings_panel_items,
      ),
    );

  // Search results
  config.ui?.search_results?.fuzzy_threshold !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.search_results.fuzzy_threshold",
        config.ui.search_results.fuzzy_threshold,
      ),
    );
  config.ui?.search_results?.sort_by !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.search_results.sort_by",
        config.ui.search_results.sort_by,
      ),
    );

  config.ui?.search_results?.search_by !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "ui.search_results.search_by",
        config.ui.search_results.search_by,
      ),
    );

  config.feature_flags?.find_my_car_active !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.find_my_car_active",
        config.feature_flags.find_my_car_active,
      ),
    );
  config.feature_flags?.user_settings_button !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.user_settings_button",
        config.feature_flags.user_settings_button,
      ),
    );

  config.feature_flags?.ar_integration !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.ar_integration",
        config.feature_flags.ar_integration,
      ),
    );

  config.feature_flags?.kiosk_mode !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.kiosk_mode",
        config.feature_flags.kiosk_mode,
      ),
    );

  config.feature_flags?.flat_earth !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.flat_earth",
        config.feature_flags.flat_earth,
      ),
    );

  config.feature_flags?.share_location !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.share_location",
        config.feature_flags.share_location,
      ),
    );

  config.feature_flags?.ticket_selector !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.ticket_selector",
        config.feature_flags.ticket_selector,
      ),
    );

  config.feature_flags?.favorites !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.favorites",
        config.feature_flags.favorites,
      ),
    );

  config.feature_flags?.speak_indications !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.speak_indications",
        config.feature_flags.speak_indications,
      ),
    );

  if (
    !safeLocalStorage.getItem(
      `${LOCALSTORAGE_PREFIX}navigation.speak_aloud_indications`,
    )
  ) {
    store.dispatch(
      setSpeakAloudIndications(config.feature_flags.speak_indications),
    );
  }

  config.feature_flags?.simplified_navigation_ui !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.simplified_navigation_ui",
        config.feature_flags.simplified_navigation_ui,
      ),
    );

  config.feature_flags?.feedback_form !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.feedback_form",
        config.feature_flags.feedback_form,
      ),
    );

  config.feature_flags?.directions_autoplay_steps_timer !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.directions_autoplay_steps_timer",
        config.feature_flags.directions_autoplay_steps_timer,
      ),
    );
  config.feature_flags?.allow_reselecting_route_target !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.allow_reselecting_route_target",
        config.feature_flags.allow_reselecting_route_target,
      ),
    );

  config.feature_flags?.browser_positioning !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.browser_positioning",
        config.feature_flags.browser_positioning,
      ),
    );

  config.feature_flags?.browser_positioning_distance !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.browser_positioning_distance",
        config.feature_flags.browser_positioning_distance,
      ),
    );

  config.feature_flags?.custom_ui !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "feature_flags.custom_ui",
        typeof config.feature_flags.custom_ui === "string"
          ? config.feature_flags.custom_ui.trim().toLowerCase() || null
          : config.feature_flags.custom_ui,
      ),
    );

  // Flight information settings
  config.flight_information?.enabled !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "flight_information.enabled",
        config.flight_information.enabled,
      ),
    );

  config.flight_information?.category_id !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "flight_information.category_id",
        config.flight_information.category_id,
      ),
    );

  config.flight_information?.polling_interval_seconds !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "flight_information.polling_interval_seconds",
        config.flight_information.polling_interval_seconds,
      ),
    );

  // Poi
  config.poi?.zoomLevelToShowText !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "poi.zoomLevelToShowText",
        config.poi.zoomLevelToShowText,
      ),
    );
  config.poi?.spacingFactor !== undefined &&
    store.dispatch(
      setInitialConfigItem("poi.spacingFactor", config.poi.spacingFactor),
    );

  //styles - colors
  config.styles?.path_color &&
    config.styles.path_color !== "" &&
    store.dispatch(
      setInitialConfigItem("styles.path_color", config.styles.path_color),
    );
  config.styles?.path_unselected_color &&
    config.styles?.path_unselected_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path_unselected_color",
        config.styles.path_unselected_color,
      ),
    );
  config.styles?.path_animation_color &&
    config.styles.path_animation_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path_animation_color",
        config.styles.path_animation_color,
      ),
    );
  config.styles?.path_arrow_color &&
    config.styles.path_arrow_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path_arrow_color",
        config.styles.path_arrow_color,
      ),
    );
  // styles - path
  config.styles?.path?.dots_gap &&
    store.dispatch(
      setInitialConfigItem("styles.path.dots_gap", config.styles.path.dots_gap),
    );
  config.styles?.path?.dots_size &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path.dots_size",
        config.styles.path.dots_size,
      ),
    );
  config.styles?.path?.show_step_arrow !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path.show_step_arrow",
        config.styles.path.show_step_arrow,
      ),
    );
  config.styles?.path?.step_fitting_zoom &&
    config.styles.path.step_fitting_zoom > 0 &&
    store.dispatch(
      setInitialConfigItem(
        "styles.path.step_fitting_zoom",
        config.styles.path.step_fitting_zoom,
      ),
    );

  // styles - map
  config.styles?.map?.labels?.color &&
    config.styles.map.labels.color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.labels.color",
        config.styles.map.labels.color,
      ),
    );
  config.styles?.map?.labels?.outline?.color !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.labels.outline.color",
        config.styles.map.labels.outline.color,
      ),
    );

  config.styles?.map.labels?.fontSize !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.labels.fontSize",
        config.styles.map.labels.fontSize,
      ),
    );

  config.styles?.map.labels?.outline.width !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.labels.outline.width",
        config.styles.map.labels.outline.width,
      ),
    );
  config.styles?.map.labels?.offset !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.labels.offset",
        config.styles.map.labels.offset,
      ),
    );

  config.styles?.map?.pois?.outline?.color_selected !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.outline.color_selected",
        config.styles.map.pois.outline.color_selected,
      ),
    );
  config.styles?.map?.pois?.iconScale !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconScale",
        config.styles.map.pois.iconScale,
      ),
    );
  config.styles?.map?.pois?.fadeOffRoutePois !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.fadeOffRoutePois",
        config.styles.map.pois.fadeOffRoutePois,
      ),
    );

  config.styles?.theme?.border_theme &&
    config.styles?.theme?.border_theme !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.border_theme",
        config.styles.theme.border_theme,
      ),
    );

  // Retrocompatibility
  if (typeof config.internal?.prefetchData === "boolean") {
    store.dispatch(
      setInitialConfigItem(
        "internal.prefetchData",
        config.internal.prefetchData ? PrefetchMode.ALL : PrefetchMode.NONE,
      ),
    );
  } else {
    !!config.internal?.prefetchData &&
      store.dispatch(
        setInitialConfigItem(
          "internal.prefetchData",
          config.internal.prefetchData,
        ),
      );
  }
  config.internal?.prefetch?.enableTilePrefetch !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "internal.prefetch.enableTilePrefetch",
        config.internal?.prefetch.enableTilePrefetch,
      ),
    );

  config.internal?.prefetch?.maxTileZoom !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "internal.prefetch.maxTileZoom",
        config.internal?.prefetch.maxTileZoom,
      ),
    );

  config.internal?.prefetch?.minTileZoom !== undefined &&
    store.dispatch(
      setInitialConfigItem(
        "internal.prefetch.minTileZoom",
        config.internal?.prefetch.minTileZoom,
      ),
    );

  // Styles
  const { colorTheme, userPreferenceColorTheme } = resolveInitialColorThemes(
    config.styles?.theme?.colorTheme ?? null,
  );

  colorTheme !== null &&
    store.dispatch(setInitialConfigItem("styles.theme.colorTheme", colorTheme));

  if (userPreferenceColorTheme !== null) {
    store.dispatch(hydrateUserPreferencesColorTheme(userPreferenceColorTheme));
  }

  config.styles?.theme?.fonts &&
    config.styles?.theme?.fonts.length > 0 &&
    store.dispatch(
      setInitialConfigItem("styles.theme.fonts", config.styles.theme.fonts),
    );

  config.styles?.theme?.warning_color &&
    config.styles?.theme?.warning_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.warning_color",
        config.styles.theme.warning_color,
      ),
    );

  // light mode with backwards compatibility
  checkBackCompatibilityLightMode(config);

  config.styles?.theme?.light_mode.brand_color &&
    config.styles?.theme?.light_mode.brand_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.brand_color",
        config.styles.theme.light_mode.brand_color,
      ),
    );

  config.styles?.theme?.light_mode.background_color &&
    config.styles?.theme?.light_mode.background_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.background_color",
        config.styles.theme.light_mode.background_color,
      ),
    );
  config.styles?.theme?.light_mode.hover_color &&
    config.styles?.theme?.light_mode.hover_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.hover_color",
        config.styles.theme.light_mode.hover_color,
      ),
    );

  // dark mode
  config.styles?.theme?.dark_mode.primary_color &&
    config.styles?.theme?.dark_mode.primary_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.primary_color",
        config.styles.theme.dark_mode.primary_color,
      ),
    );

  config.styles?.theme?.dark_mode.brand_color &&
    config.styles?.theme?.dark_mode.brand_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.brand_color",
        config.styles.theme.dark_mode.brand_color,
      ),
    );

  config.styles?.theme?.dark_mode.secondary_color &&
    config.styles?.theme?.dark_mode.secondary_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.secondary_color",
        config.styles.theme.dark_mode.secondary_color,
      ),
    );

  config.styles?.theme?.dark_mode.primary_font_color &&
    config.styles?.theme?.dark_mode.primary_font_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.primary_font_color",
        config.styles.theme.dark_mode.primary_font_color,
      ),
    );

  config.styles?.theme?.dark_mode.secondary_font_color &&
    config.styles?.theme?.dark_mode.secondary_font_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.secondary_font_color",
        config.styles.theme.dark_mode.secondary_font_color,
      ),
    );

  config.styles?.theme?.dark_mode.icon_color &&
    config.styles?.theme?.dark_mode.icon_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.icon_color",
        config.styles.theme.dark_mode.icon_color,
      ),
    );

  config.styles?.theme?.dark_mode.background_color &&
    config.styles?.theme?.dark_mode.background_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.background_color",
        config.styles.theme.dark_mode.background_color,
      ),
    );
  config.styles?.theme?.dark_mode.hover_color &&
    config.styles?.theme?.dark_mode.hover_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.dark_mode.hover_color",
        config.styles.theme.dark_mode.hover_color,
      ),
    );

  config.styles?.theme?.positioning_dot_color &&
    config.styles?.theme?.positioning_dot_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.positioning_dot_color",
        config.styles.theme.positioning_dot_color,
      ),
    );

  config.styles?.theme?.positioning_outdoor_dot_color &&
    config.styles?.theme?.positioning_outdoor_dot_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.positioning_outdoor_dot_color",
        config.styles.theme.positioning_outdoor_dot_color,
      ),
    );

  config.styles?.theme?.positioning_accuracy_color &&
    config.styles?.theme?.positioning_accuracy_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.positioning_accuracy_color",
        config.styles.theme.positioning_accuracy_color,
      ),
    );

  config.styles?.theme?.positioning_icon_color &&
    config.styles?.theme?.positioning_icon_color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.positioning_icon_color",
        config.styles.theme.positioning_icon_color,
      ),
    );

  config.styles?.map?.pois?.dots?.enabled !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.dots.enabled",
        config.styles.map.pois.dots.enabled,
      ),
    );

  config.styles?.map?.pois?.dots?.threshold &&
    config.styles.map.pois.dots.threshold !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.dots.threshold",
        config.styles.map.pois.dots.threshold,
      ),
    );

  config.styles?.map?.pois?.dots?.color &&
    config.styles.map.pois.dots.color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.dots.color",
        config.styles.map.pois.dots.color,
      ),
    );

  config.styles?.map?.pois?.iconBackground?.enabled &&
    config.styles.map.pois.iconBackground.enabled !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconBackground.enabled",
        config.styles.map.pois.iconBackground.enabled,
      ),
    );
  config.styles?.map?.pois?.iconBackground?.iconType &&
    config.styles.map.pois.iconBackground.iconType !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconBackground.iconType",
        config.styles.map.pois.iconBackground.iconType,
      ),
    );
  config.styles?.map?.pois?.iconBackground?.pinHeight &&
    config.styles.map.pois.iconBackground.pinHeight !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconBackground.pinHeight",
        config.styles.map.pois.iconBackground.pinHeight,
      ),
    );

  config.styles?.map?.pois?.iconBackground?.iconScaleFactor &&
    config.styles.map.pois.iconBackground.iconScaleFactor !== null &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconBackground.iconScaleFactor",
        config.styles.map.pois.iconBackground.iconScaleFactor,
      ),
    );

  config.styles?.map?.pois?.iconBackground?.color &&
    config.styles.map.pois.iconBackground.color !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "styles.map.pois.iconBackground.color",
        config.styles.map.pois.iconBackground.color,
      ),
    );

  config.favoritePois?.iconOutlineColor &&
    config.favoritePois?.iconOutlineColor !== "" &&
    store.dispatch(
      setInitialConfigItem(
        "favoritePois.iconOutlineColor",
        config.favoritePois.iconOutlineColor,
      ),
    );

  // Navigation
  config.navigation !== undefined &&
    store.dispatch(setInitialConfigItem("navigation", config.navigation));

  // actions
  config.actions?.actions !== undefined &&
    store.dispatch(
      setInitialConfigItem("actions.actions", config.actions.actions),
    );

  // Directions

  const defaultRouteType = getInitialValue<RouteType>(
    UserPreferenceKeys.DEFAULT_ROUTE_TYPE,
    RouteType.CHOOSE_SHORTEST,
  );

  defaultRouteType &&
    store.dispatch(
      setInitialConfigItem("directions.defaultRouteType", defaultRouteType),
    );

  config.directions?.includedTags &&
    store.dispatch(
      setInitialConfigItem(
        "directions.includedTags",
        config.directions?.includedTags,
      ),
    );

  config.directions?.excludedTags &&
    store.dispatch(
      setInitialConfigItem(
        "directions.excludedTags",
        config.directions?.excludedTags,
      ),
    );

  config.directions?.thresholdPoiDistances &&
    store.dispatch(
      setInitialConfigItem(
        "directions.thresholdPoiDistances",
        config.directions?.thresholdPoiDistances,
      ),
    );

  config.directions?.radiusPoiIndication &&
    store.dispatch(
      setInitialConfigItem(
        "directions.radiusPoiIndication",
        config.directions?.radiusPoiIndication,
      ),
    );

  config.directions?.turnThreshold &&
    store.dispatch(
      setInitialConfigItem(
        "directions.turnThreshold",
        config.directions?.turnThreshold,
      ),
    );

  config.directions?.turnBackThreshold &&
    store.dispatch(
      setInitialConfigItem(
        "directions.turnBackThreshold",
        config.directions?.turnBackThreshold,
      ),
    );

  config.directions?.distanceToDiscriminateLookToTurn &&
    store.dispatch(
      setInitialConfigItem(
        "directions.distanceToDiscriminateLookToTurn",
        config.directions?.distanceToDiscriminateLookToTurn,
      ),
    );

  config.directions?.slightTurnThreshold &&
    store.dispatch(
      setInitialConfigItem(
        "directions.slightTurnThreshold",
        config.directions?.slightTurnThreshold,
      ),
    );

  config.directions?.longRouteWarning.categoriesIds &&
    store.dispatch(
      setInitialConfigItem(
        "directions.longRouteWarning.categoriesIds",
        config.directions?.longRouteWarning.categoriesIds,
      ),
    );
  config.directions?.longRouteWarning.distanceThreshold &&
    store.dispatch(
      setInitialConfigItem(
        "directions.longRouteWarning.distanceThreshold",
        config.directions?.longRouteWarning.distanceThreshold,
      ),
    );
  config.directions?.longRouteWarning.timeThreshold &&
    store.dispatch(
      setInitialConfigItem(
        "directions.longRouteWarning.timeThreshold",
        config.directions?.longRouteWarning.timeThreshold,
      ),
    );
  config.directions?.longRouteWarning.maxDistanceToNearPois &&
    store.dispatch(
      setInitialConfigItem(
        "directions.longRouteWarning.maxDistanceToNearPois",
        config.directions?.longRouteWarning.maxDistanceToNearPois,
      ),
    );

  config.informativeMessages?.showRouteDelayWarningToReachTheFlight !==
    undefined &&
    store.dispatch(
      setInitialConfigItem(
        "informativeMessages.showRouteDelayWarningToReachTheFlight",
        config.informativeMessages.showRouteDelayWarningToReachTheFlight,
      ),
    );
};

export const getConfigToDownload = (fullConfig = true) => {
  //Mandatory fields
  const config = store.getState().config;
  const auth = config.auth;
  const session = store.getState().session;

  // Compose JSON with fields that are different from default ...
  let cleanedConfig = fullConfig
    ? (JSON.parse(JSON.stringify(config)) as ConfigState)
    : (difference(defaultConfig as any, config as any) as Partial<ConfigState>);

  // ... plus mandatory fields
  cleanedConfig = { auth, ...cleanedConfig };

  // Avoid exporting language configuration
  delete cleanedConfig?.i18n?.language;
  // Avoid exporting jwt
  delete cleanedConfig?.auth?.jwt;
  // Avoid exporting performance mode
  delete cleanedConfig.internal.performanceMode;

  if (session.productionApiKeySettings.mode === "none") {
    cleanedConfig.auth.production_api_key = "";
  }

  //set prod apikey as new apikey or delete it
  if (
    [
      SitumApiPermissionLevel.CARTOGRAPHY_READ_WRITE,
      SitumApiPermissionLevel.READ_WRITE,
    ].includes(store.getState().app.apiPermissionLevel)
  ) {
    if (cleanedConfig.auth.production_api_key !== null) {
      cleanedConfig.auth.api_key = cleanedConfig.auth.production_api_key;
    } else delete cleanedConfig.auth.api_key;
  }

  return cleanedConfig;
};

function checkBackCompatibilityLightMode(config: any) {
  if (
    config?.styles?.theme?.primary_color &&
    config?.styles?.theme.primary_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.primary_color",
        config?.styles?.theme.primary_color,
      ),
    );
    delete config?.styles?.theme.primary_color;
  } else if (
    config.styles?.theme?.light_mode?.primary_color &&
    config.styles.theme.light_mode.primary_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.primary_color",
        config.styles.theme.light_mode.primary_color,
      ),
    );
  }

  if (
    config?.styles?.theme?.secondary_color &&
    config?.styles?.theme.secondary_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.secondary_color",
        config?.styles?.theme.secondary_color,
      ),
    );
    delete config?.styles?.theme.secondary_color;
  } else if (
    config.styles?.theme?.light_mode?.secondary_color &&
    config.styles.theme.light_mode.secondary_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.secondary_color",
        config.styles.theme.light_mode.secondary_color,
      ),
    );
  }

  if (
    config?.styles?.theme?.primary_font_color &&
    config?.styles?.theme.primary_font_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.primary_font_color",
        config?.styles?.theme.primary_font_color,
      ),
    );
    delete config?.styles?.theme.primary_font_color;
  } else if (
    config.styles?.theme?.light_mode?.primary_font_color &&
    config.styles.theme.light_mode.primary_font_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.primary_font_color",
        config.styles.theme.light_mode.primary_font_color,
      ),
    );
  }

  if (
    config?.styles?.theme?.secondary_font_color &&
    config?.styles?.theme.secondary_font_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.secondary_font_color",
        config?.styles?.theme.secondary_font_color,
      ),
    );
    delete config?.styles?.theme.secondary_font_color;
  } else if (
    config.styles?.theme?.light_mode?.secondary_font_color &&
    config.styles.theme.light_mode.secondary_font_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.secondary_font_color",
        config.styles.theme.light_mode.secondary_font_color,
      ),
    );
  }

  if (
    config?.styles?.theme?.icon_color &&
    config?.styles?.theme.icon_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.icon_color",
        config?.styles?.theme.icon_color,
      ),
    );
    delete config?.styles?.theme.icon_color;
  } else if (
    config.styles?.theme?.light_mode?.icon_color &&
    config.styles.theme.light_mode.icon_color !== ""
  ) {
    store.dispatch(
      setInitialConfigItem(
        "styles.theme.light_mode.icon_color",
        config.styles.theme.light_mode.icon_color,
      ),
    );
  }
}

/**
 * Handles auth credentials received from the webview API when `wait_for_auth`
 * is enabled
 */
export const handleAuthChange = (payload: {
  apikey?: string;
  jwt?: string;
}) => {
  const isWaitForAuthEnabled =
    new URLSearchParams(window.location.search).get("wait_for_auth") === "true";

  if (!isWaitForAuthEnabled) return;

  const state = store.getState();
  const auth = state.config.auth;

  const authReady = selectAuthReady(store.getState());
  const currentOrganization = selectCurrentOrganization(store.getState());

  // No credentials have been received at any point
  if (!authReady) {
    if (payload?.jwt) {
      store.dispatch(setConfigItem("auth.jwt", payload.jwt));
      store.dispatch(setConfigItem("auth.production_api_key", null));
      store.dispatch(setConfigItem("auth.api_key", null));
      store.dispatch(setAuthReady(true));
      return;
    }
    if (payload?.apikey) {
      store.dispatch(setConfigItem("auth.production_api_key", payload.apikey));
      store.dispatch(setConfigItem("auth.api_key", payload.apikey));
      store.dispatch(setConfigItem("auth.jwt", null));
      store.dispatch(setAuthReady(true));
    }
    return;
  }

  // Reject credentials that haven't changed
  if (payload.jwt && auth.jwt === payload.jwt) return;
  if (payload.apikey && auth.api_key === payload.apikey) return;
  if (!currentOrganization.id) return;

  // Reject credentials that belong to a different organization
  if (payload?.apikey) {
    verifyOrganization({
      apiKey: payload.apikey,
      domain: auth.domain,
      organizationId: currentOrganization.id,
    });
    return;
  }

  if (payload?.jwt) {
    verifyOrganization({
      domain: auth.domain,
      jwt: payload.jwt,
      organizationId: currentOrganization.id,
    });
    return;
  }
};
